import { useCallback, useRef } from 'react';
import { useToggle } from 'react-use';
import cn from 'classnames';
import { ShiftSiblingData } from '@lp/features/Scheduler/types';
import { ShiftButton, UnblockShiftPopover } from '@lp/features/Scheduler/components';
import { CCShiftOptionType } from '@lp/features/Scheduler/SchedulerClinicalCoordinators/types';
import { CCShiftCellDropdown } from '@lp/features/Scheduler/SchedulerClinicalCoordinators/components';
import {
  getCCShiftColors,
  getCCShiftTypePrimaryText,
  getCCShowHrIcon,
} from '@lp/features/Scheduler/SchedulerClinicalCoordinators/utils';
import { CCShift, CCShiftSelectPayload, ShiftFixationType } from '@/api/apis/resources/schedule';
import { NameByValue } from '@/types';

type CCShiftCellProps = {
  shiftData: CCShift;
  dropdownList: NameByValue<CCShiftOptionType>[];
  siblingData?: ShiftSiblingData;
  onShiftChange: (shiftPayload?: CCShiftSelectPayload) => void;
  lighten?: boolean;
  isCentered?: boolean;
  readonly?: boolean;
  canReset?: boolean;
  canUnblock?: boolean;
  onUnblock?: () => void;
};

export const CCShiftCell = ({
  onShiftChange,
  shiftData,
  dropdownList,
  siblingData,
  lighten,
  isCentered,
  readonly,
  canReset,
  canUnblock,
  onUnblock,
}: CCShiftCellProps) => {
  const elRef = useRef<HTMLButtonElement>(null);
  const [isOpen, toggleIsOpen] = useToggle(false);
  const { isEditable, fixationType } = shiftData;
  const isBlockedRequired = !isEditable && fixationType === ShiftFixationType.Required;
  const disabled = !isEditable || readonly;
  const showContent = !siblingData?.hasPrev;
  const primaryText = getCCShiftTypePrimaryText(shiftData);
  const colors = getCCShiftColors(shiftData);

  const canShowUnblock = isBlockedRequired && canUnblock && !readonly;

  const onClick = useCallback(() => {
    toggleIsOpen();
  }, [toggleIsOpen]);

  return (
    <>
      <ShiftButton
        ref={elRef}
        lighten={lighten}
        disabled={disabled && !canShowUnblock}
        siblingData={siblingData}
        isCentered={isCentered}
        className={cn(colors.bg, colors.text, colors.border)}
        onClick={onClick}
        showHrIcon={getCCShowHrIcon(shiftData)}
      >
        {showContent && (
          <div className="flex flex-col items-start w-full z-1">
            <div className="flex items-center justify-between gap-2 w-full">{primaryText}</div>
          </div>
        )}
      </ShiftButton>

      {isOpen && canShowUnblock && onUnblock && (
        <UnblockShiftPopover triggerRef={elRef} isOpen={isOpen} onOpenChange={toggleIsOpen} onUnblock={onUnblock} />
      )}

      {isOpen && !canShowUnblock && (
        <CCShiftCellDropdown
          triggerRef={elRef}
          isOpen={isOpen}
          onOpenChange={toggleIsOpen}
          shiftData={shiftData}
          onShiftChange={onShiftChange}
          canReset={canReset}
          optionList={dropdownList}
        />
      )}
    </>
  );
};
